import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  PopoverShell,
  SegmentedControl,
  SparkIcon,
  Text,
  getOverlayPortalParent,
  useAnchoredPanelPosition,
  useEscapeKey,
  useOutsideClick,
} from '@spark';
import {
  COST_PERIOD_OPTIONS,
  INSTANCE_PURCHASING_OPTIONS,
  DEFAULT_COST_CONFIG,
} from './data/performanceMetricsApi';

/**
 * Popover for choosing cost period (Hourly/Daily/Monthly) and instance
 * purchasing option (On Demand/Spot). Triggered by the "Cost configuration"
 * link in the Performance filters row.
 *
 * Portaled like origin/main's Ant Design Popover: the panel is attached to
 * document.body (or the Spark modal root) with position:fixed + z-index 1100
 * so DataTable stacking cannot swallow clicks. Outside click discards the
 * draft; Done commits it — same apply/cancel contract as main.
 *
 * Visual contract (Spark Popover + SegmentedControl):
 *   - Top tip aligned to the end, pointing at the trigger.
 *   - Selected segment: outlined (blue border + blue text on white).
 *   - Section titles in semibold dark text, all content left-aligned.
 *   - Compact width hugging the segmented controls.
 *   - "Done" ghost action at the BOTTOM-LEFT.
 *
 * @param {Object} props
 * @param {import('./data/performanceMetricsApi').CostConfig} props.value
 * @param {(next: import('./data/performanceMetricsApi').CostConfig) => void} props.onChange
 */
export default function CostConfigPopover({ value = DEFAULT_COST_CONFIG, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  useOutsideClick([triggerRef, panelRef], close, open);
  useEscapeKey(close, open);
  const panelStyle = useAnchoredPanelPosition(triggerRef, panelRef, open, 'end');

  const handleToggle = () => {
    if (!open) setDraft(value);
    setOpen((prev) => !prev);
  };

  const handleDone = () => {
    onChange?.(draft);
    setOpen(false);
  };

  const panel = (
    <div
      ref={panelRef}
      className="fs-unmask"
      role="dialog"
      aria-label="Cost Configuration"
      style={{
        ...panelStyle,
        position: 'fixed',
        zIndex: 1100,
        pointerEvents: 'auto',
        width: 'max-content',
      }}
    >
      <PopoverShell tip tipDirection="top" tipAlign="end">
        <div className="spark-popover-stack">
          <div className="spark-popover-stack-section">
            <Text variant="base-semibold" as="div">
              Cost Configuration
            </Text>
            <SegmentedControl
              style="outlined"
              items={COST_PERIOD_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
              activeId={draft.period}
              onChange={(id) => setDraft((d) => ({ ...d, period: id }))}
            />
          </div>

          <div className="spark-popover-stack-section">
            <Text variant="base-semibold" as="div">
              Instance Lifecycle
            </Text>
            <SegmentedControl
              style="outlined"
              items={INSTANCE_PURCHASING_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
              activeId={draft.lifecycle}
              onChange={(id) => setDraft((d) => ({ ...d, lifecycle: id }))}
            />
          </div>

          <div className="spark-popover-stack-footer">
            <Button variant="ghost" size="tiny" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      </PopoverShell>
    </div>
  );

  return (
    <div ref={triggerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <Button
        variant="ghost"
        size="small"
        className="fs-unmask"
        aria-expanded={open}
        aria-haspopup="dialog"
        startIcon={<SparkIcon slug="edit" size="small" />}
        onClick={handleToggle}
      >
        Cost configuration
      </Button>
      {open ? createPortal(panel, getOverlayPortalParent(triggerRef.current)) : null}
    </div>
  );
}
